import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { clone, defaults } from 'lodash';
import { v4 } from 'uuid';
import {
  hideGlobalBanner,
  setGlobalBanner,
} from '../../../../store/app.actions';
import { BrowserManagerDataDispatcher } from '../../../browser-manager-data/browser-manager-data.dispatcher';
import { LocalStorageService } from '../../../browser-manager-data/local-storage.service';
import { ErrorService } from '../../../error-reporting/error.service';
import { LocalizationService } from '../../../localization/localization.service';
import { supportUrlsLocalized, URLS } from '../../constants/urls.constant';
import { LoggerService } from '../../services/logger/logger.service';
import { GlobalBannerComponent } from './global-banner.component';
import {
  GlobalBannerStatesEnum,
  GlobalBannerTypesEnum,
} from './global-banner.enum';
import { IGlobalBanner } from './global-banner.interface';

export const BANNERS_STATES_KEY = 'BANNERS_STATES';

@Injectable()
export class GlobalBannerService {
  constructor(
    private _store: Store<any>,
    private _loggerService: LoggerService,
    private _translateService: TranslateService,
    private _localizationService: LocalizationService,
    private _errorService: ErrorService,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _localStorageService: LocalStorageService
  ) {}

  showBanner(globalBanner: IGlobalBanner, bannerStateKey?: string): string {
    const bannerState = bannerStateKey
      ? this._getBannerState(bannerStateKey)
      : GlobalBannerStatesEnum.none;

    if (bannerState !== GlobalBannerStatesEnum.dismissed) {
      const defaultBanner: IGlobalBanner = {
        id: v4(),
        dismissible: true,
        type: GlobalBannerTypesEnum.info,
        onDismiss: () => this.hideBanner(),
      };
      const banner = defaults(clone(globalBanner), defaultBanner);

      this._loggerService.log('displaying banner:', banner);

      this._store.dispatch(setGlobalBanner(banner));

      return banner.id;
    } else {
      return null;
    }
  }

  hideBanner(id?: string) {
    this._store.dispatch(hideGlobalBanner(id));
  }

  showSystemErrorBanner(
    error: Error,
    messageHtml?: string,
    onDismiss?: (bannerId: string, component: GlobalBannerComponent) => void
  ): string {
    this._errorService.alertSystem(error);

    // User cannot dismiss this banner to continue.
    // Observable used for change detection is already terminated when it pushed error notification.
    // Encouraging user to reload the app.
    return this.showBanner({
      type: GlobalBannerTypesEnum.error,
      message:
        messageHtml ||
        this._translateService.instant('ERROR.SERVER_ERROR', {
          url: supportUrlsLocalized(
            URLS.SUPPORT,
            this._localizationService.getLocale()
          ),
        }),
      containsHtml: true,
      dismissible: true,
      isSticky: true,
      onDismiss,
      e2eId: 'error-banner',
    });
  }

  setBannerStateDismissed(bannerStateKey: string) {
    const states =
      JSON.parse(this._localStorageService.getItem(BANNERS_STATES_KEY)) || {};

    states[bannerStateKey] = GlobalBannerStatesEnum.dismissed;

    this._browserManagerDataDispatcher.setLocalStorageItem(
      BANNERS_STATES_KEY,
      JSON.stringify(states)
    );
  }

  private _getBannerState(bannerStateKey: string): GlobalBannerStatesEnum {
    const states = JSON.parse(
      this._localStorageService.getItem(BANNERS_STATES_KEY)
    );

    return states ? states[bannerStateKey] : GlobalBannerStatesEnum.none;
  }
}
